import { useMemo, useState } from 'react'
import { displayTitle } from '../../lib/displayName'
import { omitTransientProps } from '../../lib/omitTransientProps'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import {
  MinimalAlert,
  MinimalBadge,
  MinimalButton,
  MinimalCard,
  MinimalEmptyState,
  MinimalFilterBar,
  MinimalOption,
  MinimalStatCard,
  type MinimalTone,
} from '@ovasabi/ui-minimal'
import {
  Money,
  PageHeader,
  Screen,
  SectionHeading,
} from '../../components/chow'
import { ClockIcon, RouteIcon } from '../../components/icons'
import { KitchenBadge } from '../../components/chow/KitchenBadge'
import {
  useChefMenuSnapshot,
  useChowProfileSnapshot,
  useDishSnapshot,
  useMarketplaceOrderSnapshot,
  useVendorProfileSnapshot,
} from '../../generated/prototypeRuntime'
import { commandPayload, commands, isLiveRuntime, mediaUrl } from '../../lib/runtime'
import { scheduleMessage } from '../../lib/scheduling'
import {
  effectiveManualOpen,
  effectiveSchedule,
  isOpenNow,
  openWindowToday,
  parseSchedule,
  rememberVendorStatus,
  to12h,
} from '../../lib/vendorSchedule'
import {
  ChefStyle,
  confirmedUpsert,
  enumLabel,
  enumShort,
  isoDay,
  type StoreRecord,
} from './chefShared'
import { buildPrepDays, prepDayKey, isConfirmedToday, isPrepOrder, isUpcomingMeal } from './chefDashboard'
import { useChefPayouts, weekEarnedMinor } from './chefPayouts'
import { useUnreadOrdersMap, useUnreadThreadCount } from './unreadMessages'
import { useBackNav, useChooseChow, usePageContext } from './ChooseChowContext'
import { orderReference, orderSummary, useOrderItemNames } from './orderDisplay'
import { useChefOrderDetail, useOrderAdvance } from './chefOrderState'
import { uniqueDishLibrary } from './dishLibrary'
import { useChefPayoutAccount } from './ChefPayoutAccount'
import { compareTimestamps, isAfterTimestamp } from '../../lib/timestamp'
import { OrderAlertsNudge } from '../notifications/OrderAlertsNudge'
import { KitchenSetupChecklist } from './KitchenSetupChecklist'
import { useHasPhone } from './profilePhone'
import { useKitchenCatalog } from './kitchenCatalog'
import { ShareKitchenCard } from './ShareKitchenCard'

/**
 * The chef-side dispatch flow: one step per `marketplace:set_order_status`
 * command. In live mode the projection drives the row's status; offline we echo
 * it locally so the prototype advances without a backend.
 *
 * These keys and targets are the ORDER STATUS VOCABULARY the backend stores
 * (choosechow_orders.status) and the transitions its lifecycle permits a
 * merchant to make — see internal/service/marketplace/order_lifecycle.go. They
 * used to be prototype words (`placed`, `dispatched`) that no real order ever
 * holds and the service rejects: a paid order offered the chef no button at
 * all, and the handoff button failed validation into a swallowed catch while
 * the UI optimistically showed it advanced.
 */
const DISPATCH_FLOW: Record<string, { next: string; label: string }> = {
  paid: { next: 'accepted', label: 'Accept order' },
  accepted: { next: 'preparing', label: 'Start prep' },
  preparing: { next: 'ready', label: 'Mark ready' },
  ready: { next: 'delivery_requested', label: 'Arrange delivery' },
  delivery_requested: { next: 'in_delivery', label: 'Confirm courier collection' },
  in_delivery: { next: 'completed', label: 'Mark delivered' },
}

const orderStep = (order: StoreRecord) => {
  const status = enumShort(order.status, 'paid')
  return status === 'ready' && enumShort(order.delivery_choice) === 'self_pickup'
    ? { next: 'completed', label: 'Mark customer collection' }
    : DISPATCH_FLOW[status]
}

type DispatchFilter = 'today' | 'all' | 'prep' | 'paid' | 'accepted' | 'preparing' | 'ready' | 'delivery_requested' | 'in_delivery' | 'scheduled' | 'timeline'

const DISPATCH_FILTERS: readonly MinimalOption<DispatchFilter>[] = [
  { value: 'all', label: 'Active orders' },
  { value: 'prep', label: 'Prep queue' },
  { value: 'timeline', label: 'Prep timeline' },
  { value: 'today', label: 'Today' },
  { value: 'paid', label: 'New' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'preparing', label: 'Prep' },
  { value: 'ready', label: 'Ready' },
  { value: 'delivery_requested', label: 'Handoff' },
  { value: 'in_delivery', label: 'Delivering' },
  { value: 'scheduled', label: 'Scheduled' },
]

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;

  /* 760px sits just under the page stop, so from('page') would move it. */
  @media (min-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  /* 1120px matches no named stop; it sits between the desk and wide stops. */
  @media (min-width: 1120px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`

/* ── Prep timeline (Meal Calendar, chef side) ──────────────────────────
   The kitchen's look-ahead: upcoming scheduled meals grouped by date, split
   into firm (paid+) and held (pending_payment reservations) so the chef cooks
   and shops against a real timeline. Reads the same orders the dispatch queue
   does — one record, two views. */

const PrepStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(136px, 1fr));
  gap: 10px;
`

/* Filter the styling-only flag before it reaches the anchor. React 19 treats
   `$empty` as an invalid DOM attribute. */
const PrepDay = styled(omitTransientProps(Link))<{ $empty: boolean }>`
  display: grid;
  gap: 4px;
  min-height: 112px;
  background: ${minimalVars.color.bgSurfaceAlt};
  border: 1px solid ${minimalVars.color.borderSubtle};
  border-radius: ${minimalVars.radius.md};
  padding: 14px;
  opacity: ${({ $empty }) => ($empty ? 0.85 : 1)};
  text-decoration: none;
  transition: border-color 160ms ease, background-color 160ms ease, transform 160ms ease;
  &:focus-visible { outline: 2px solid ${minimalVars.color.brand}; outline-offset: 2px; }
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      border-color: ${minimalVars.color.brand};
      background: ${minimalVars.color.bgSurface};
      transform: translateY(-1px);
    }
  }
`

const PrepWeekday = styled.div`
  font-size: ${minimalVars.typography.metaSize};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${minimalVars.color.textTertiary};
`

const PrepDate = styled.div`
  font-size: ${minimalVars.typography.captionSize};
  color: ${minimalVars.color.textTertiary};
`

const PrepCount = styled.div`
  font-family: ${minimalVars.typography.displayFamily};
  font-weight: ${minimalVars.typography.weightBold};
  /* 24px sits 9% from h2Size and 50% from h1Size, so no token matches. */
  font-size: 1.5rem;
  line-height: 1.1;
  margin: 3px 0 4px;
  font-variant-numeric: tabular-nums;
  color: ${minimalVars.color.textPrimary};
`

const PrepSplit = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: ${minimalVars.typography.metaSize};
  color: ${minimalVars.color.textSecondary};
  font-variant-numeric: tabular-nums;
`

const PrepDot = styled.span<{ $tone: 'firm' | 'held' }>`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 2px;
  margin-right: 4px;
  vertical-align: middle;
  background: ${({ $tone }) => ($tone === 'firm' ? minimalVars.color.success : minimalVars.color.warning)};
`

const PrepTimeline = ({ orders }: { orders: readonly StoreRecord[] }) => {
  const days = useMemo(() => buildPrepDays(orders), [orders])
  const hasAny = days.some((d) => d.total > 0)
  return (
    <PremiumChefCard $padding="sm">
      <SectionHeading title="Prep timeline · next 7 days" />
      <ChefStyle.Sub>
        {hasAny
          ? 'What to cook, by pickup day. Cook only orders that are paid.'
          : 'When someone orders, it shows up here. Finished orders move off this list.'}
      </ChefStyle.Sub>
      <div style={{ marginTop: 10 }}>
        <PrepStrip>
          {days.map((d) => (
            <PrepDay key={d.key} $empty={d.total === 0} to={`/orders?filter=timeline&date=${d.key}`} aria-label={`${d.weekday} ${d.label}: ${d.total} orders, ${d.firm} confirmed, ${d.held} awaiting payment`}>
              <PrepWeekday>{d.weekday}</PrepWeekday>
              <PrepDate>{d.label}</PrepDate>
              <PrepCount>{d.total}</PrepCount>
              <PrepSplit>
                <span><PrepDot $tone="firm" />{d.firm} paid</span>
                <span><PrepDot $tone="held" />{d.held} awaiting payment</span>
              </PrepSplit>
              {d.firm > 0 && <ChefStyle.Sub>
                {d.stages.paid} new · {d.stages.accepted} accepted<br />
                {d.stages.preparing} preparing · {d.stages.ready} ready
              </ChefStyle.Sub>}
            </PrepDay>
          ))}
        </PrepStrip>
      </div>
    </PremiumChefCard>
  )
}

const KpiRow = styled(motion.div)`
  display: grid;
  gap: 12px;
`

const OpsPanel = styled.div`
  display: grid;
  gap: ${minimalVars.space.sm};
  background: ${minimalVars.color.bgSurface};
  border: 1px solid ${minimalVars.color.borderSubtle};
  border-radius: ${minimalVars.radius.lg};
  padding: 20px;
  box-shadow: var(--chow-shadow-panel, 0 4px 16px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02));
`

const OpsAnalysisGrid = styled.div`
  display: grid;
  gap: ${minimalVars.space.sm};

  /* 760px sits just under the page stop, so from('page') would move it. */
  @media (min-width: 760px) {
    grid-template-columns: 1.1fr 0.9fr;
  }
`

const OpsBars = styled.div`
  display: grid;
  gap: 10px;
`

const OpsBarRow = styled.div`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr) 42px;
  align-items: center;
  gap: 10px;
  color: ${minimalVars.color.textSecondary};
  font-size: ${minimalVars.typography.captionSize};
`

const OpsTrack = styled.span`
  height: 10px;
  border-radius: ${minimalVars.radius.pill};
  background: ${minimalVars.color.bgSurfaceAlt};
  overflow: hidden;
`

const OpsFill = styled.span<{ $value: number }>`
  display: block;
  width: ${({ $value }) => `${Math.max(6, Math.min(100, $value))}%`};
  height: 100%;
  border-radius: inherit;
  background: ${minimalVars.color.brand};
`

const OpsRevenue = styled.div`
  display: grid;
  align-content: center;
  gap: 6px;
  padding: ${minimalVars.space.sm};
  border: 1px solid ${minimalVars.color.borderSubtle};
  border-radius: ${minimalVars.radius.md};
  background: ${minimalVars.color.bgSurfaceAlt};
`

const OpsRevenueLabel = styled.span`
  color: ${minimalVars.color.textTertiary};
  font-size: ${minimalVars.typography.metaSize};
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`

const OpsRevenueValue = styled.span`
  font-family: ${minimalVars.typography.displayFamily};
  font-weight: ${minimalVars.typography.weightBold};
  /* This clamp runs 25.6px to 38.4px. h1Size runs 28px to 40px on a wider
     slope, so it would resize the figure at both ends. */
  font-size: clamp(1.6rem, 4vw, 2.4rem);
  line-height: 1.05;
  color: ${minimalVars.color.textPrimary};
`

const ChefOpsSnapshot = ({
  ordersToday,
  prepCount,
  scheduledThisWeek,
  weekIncomeMinor,
}: {
  ordersToday: number
  prepCount: number
  scheduledThisWeek: number
  weekIncomeMinor: number
}) => {
  const max = Math.max(ordersToday, prepCount, scheduledThisWeek, 1)
  const rows = [
    ['Today', ordersToday],
    ['Prep queue', prepCount],
    ['Scheduled', scheduledThisWeek],
  ] as const
  return (
    <OpsPanel>
      <SectionHeading title="Operations snapshot" />
      <OpsAnalysisGrid>
        <OpsBars aria-label="Order analysis">
          {rows.map(([label, value]) => (
            <OpsBarRow key={label}>
              <span>{label}</span>
              <OpsTrack>
                <OpsFill $value={(value / max) * 100} />
              </OpsTrack>
              <strong>{value}</strong>
            </OpsBarRow>
          ))}
        </OpsBars>
        <OpsRevenue>
          <OpsRevenueLabel>This week revenue</OpsRevenueLabel>
          <OpsRevenueValue>₦{(weekIncomeMinor / 100).toLocaleString()}</OpsRevenueValue>
          <ChefStyle.Sub>Money from your paid orders.</ChefStyle.Sub>
        </OpsRevenue>
      </OpsAnalysisGrid>
    </OpsPanel>
  )
}

/* Right-aligned icon link footer for stat cards, so "Manage" / "Build menu"
   buttons sit consistently in the bottom-right of every card. */
const StatLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  padding: 4px 0 0;
  border: 0;
  background: transparent;
  color: ${minimalVars.color.brand};
  font: inherit;
  font-size: ${minimalVars.typography.captionSize};
  font-weight: ${minimalVars.typography.weightSemibold};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${minimalVars.color.brand};
    outline-offset: 2px;
    border-radius: 2px;
  }
`

const StatCardLink = styled(Link)`
  display: block;
  color: inherit;
  text-decoration: none;
  border-radius: ${minimalVars.radius.md};

  [data-minimal='StatCard'] {
    min-height: 116px;
    align-content: start;
  }

  &:focus-visible { outline: 2px solid ${minimalVars.color.brand}; outline-offset: 3px; }
  &:hover { box-shadow: 0 0 0 1px ${minimalVars.color.brand}; }
`

const StatLinkRow = styled.div`
  display: flex;
  justify-content: flex-end;
`

// motion would pass `$` props to the DOM; styled-components used to strip them.
const PremiumChefCard = styled(omitTransientProps(motion.div))<{ $padding?: 'sm' | 'md' }>`
  background: ${minimalVars.color.bgSurface};
  border: 1px solid ${minimalVars.color.borderSubtle};
  border-radius: ${minimalVars.radius.lg};
  padding: ${({ $padding = 'sm' }) => ($padding === 'md' ? minimalVars.space.md : minimalVars.space.sm)};
  box-shadow: var(--chow-shadow-panel, 0 4px 16px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02));
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  /* 599px matches no named stop; it sits just under the page stop. */
  @media (max-width: 599px) {
    padding: ${({ $padding = 'sm' }) => ($padding === 'md' ? minimalVars.space.sm : minimalVars.space.sm)};
  }
`

/* Live open/closed indicator: green when the kitchen is taking orders right
   now, amber when the chef is online but outside their working hours, grey when
   toggled off. This is what makes "online show green" on the kitchen page. */
const StatusDot = styled.span<{ $tone: 'open' | 'away' | 'off' }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: ${({ $tone }) =>
    $tone === 'open'
      ? minimalVars.color.success
      : $tone === 'away'
        ? minimalVars.color.warning
        : minimalVars.color.textTertiary};
  box-shadow: ${({ $tone }) =>
    $tone === 'open' ? `0 0 0 3px ${minimalVars.color.successSoft}` : 'none'};
`

const StatusLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${minimalVars.space['2xs']};
  margin-top: 2px;
`

/* Seven-day week-at-a-glance strip for the Working hours card: one chip per day,
   tinted when open, ringed on today — so the chef reads their week without
   opening the editor. Mirrors the rounded-tile language used across the app. */
const WeekStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
  margin: 12px 0 4px;
`
const DayChip = styled.div<{ $open: boolean; $today: boolean }>`
  display: grid;
  justify-items: center;
  gap: 3px;
  padding: 8px 2px;
  border-radius: ${minimalVars.radius.md};
  border: 1px solid
    ${({ $today }) => ($today ? minimalVars.color.brand : minimalVars.color.borderSubtle)};
  background: ${({ $open }) => ($open ? minimalVars.color.brandSoft : minimalVars.color.bgSurfaceAlt)};
`
const DayChipLetter = styled.span<{ $open: boolean }>`
  font-family: ${minimalVars.typography.displayFamily};
  font-weight: ${minimalVars.typography.weightBold};
  /* 12.8px sits 1.6% from captionSize, so the chip letter takes that token. */
  font-size: ${minimalVars.typography.captionSize};
  line-height: 1;
  color: ${({ $open }) => ($open ? minimalVars.color.brand : minimalVars.color.textTertiary)};
`
const DayChipState = styled.span`
  /* 9.3px sits under the 12px metaSize floor. The chip stacks two lines, so
     the state keeps a fixed size below the scale. */
  font-size: 0.58rem;
  letter-spacing: 0.02em;
  color: ${minimalVars.color.textTertiary};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

const WEEKDAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const
/** Compact opening hour for a chip, e.g. "09:00" → "9a", "17:00" → "5p". */
const shortHour = (hhmm: string): string => {
  const [h] = hhmm.split(':').map(Number)
  if (!Number.isFinite(h)) return ''
  const period = h < 12 ? 'a' : 'p'
  const hour12 = h % 12 === 0 ? 12 : h % 12
  return `${hour12}${period}`
}

const ReadinessGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${minimalVars.space.xs};

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`

const ReadinessCard = styled(PremiumChefCard)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${minimalVars.space.xs};
`

const CommandHero = styled(PremiumChefCard)`
  display: grid;
  gap: ${minimalVars.space.sm};
`

const CommandTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${minimalVars.space.xs};
  flex-wrap: wrap;
`

const CommandActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${minimalVars.space.xs};
  flex-wrap: wrap;
  padding-top: ${minimalVars.space.xs};
  border-top: 1px solid ${minimalVars.color.borderSubtle};
`

/* ── Chef home: daily snapshot + status toggle ──────────────────────── */

export const ChefHomePage = () => {
  const navigate = useNavigate()
  const { profileId } = useChooseChow()
  const page = usePageContext('/')
  const unreadThreads = useUnreadThreadCount(profileId, 'chef')

  const orders = useMarketplaceOrderSnapshot() as { records: readonly StoreRecord[] }
  const menus = useChefMenuSnapshot() as { records: readonly StoreRecord[] }
  const dishes = useDishSnapshot() as { records: readonly StoreRecord[] }
  // This week's earnings come from the payout ledger, the only authoritative
  // record of chef money (chefPayouts.ts).
  const { data: payouts } = useChefPayouts()
  const vendors = useVendorProfileSnapshot() as { records: readonly StoreRecord[] }

  /* The chef's own vendor row carries the persisted open/closed sign and the
     weekly schedule. Reading it here — instead of a local `useState(true)` —
     is what stops the kitchen page from claiming "Open" while the public chef
     card (which reads the same row) shows "Closed". */
  const vendorRecord = useMemo(() => {
    const mine = vendors.records.filter((v) => String(v.profile_id ?? '') === profileId)
    /* A synthetic `vendor-<profileId>` row (written before the real one was
       known) can sit beside the projected row; `find` took whichever came
       first, so the sign could jump between them. The real row wins. */
    return mine.find((v) => !String(v.id ?? '').startsWith('vendor-')) ?? mine[0]
  }, [vendors.records, profileId])

  /* Manual toggle, read exactly as customers read it (missing = closed). */
  const manualOpen = effectiveManualOpen(profileId, vendorRecord)
  const [statusPending, setStatusPending] = useState(false)
  const [statusError, setStatusError] = useState('')
  const schedule = effectiveSchedule(
    profileId,
    parseSchedule(vendorRecord?.schedule),
    vendorRecord?.updated_at,
  )
  const withinHours = isOpenNow(schedule)
  /* Effective open = toggled on AND inside working hours. Customers can order
     now only in this state; outside hours they can still schedule ahead. */
  const effectiveOpen = manualOpen && withinHours

  const mineMenus = useMemo(
    () =>
      menus.records.filter((m) => {
        const owner = String(m.chef_profile_id ?? '')
        return Boolean(profileId) && owner === profileId
      }),
    [menus.records, profileId],
  )

  const mineOrders = useMemo(
    () =>
      orders.records.filter((o) => {
        if (isLiveRuntime()) {
          const id = String(o.id ?? '')
          if (id.startsWith('order-') || id.includes('-prototype')) return false
          return String(o.merchant_profile_id ?? '') === profileId
        }
        return true
      }),
    [orders.records, profileId],
  )

  const activeMenus = mineMenus.filter((m) => enumShort(m.status) === 'published').length
  const draftMenus = mineMenus.filter((m) => enumShort(m.status, 'draft') === 'draft').length

  const ordersToday = mineOrders.filter(isConfirmedToday).length
  const prepQueue = mineOrders.filter(isPrepOrder)
  const scheduledThisWeek = mineOrders.filter(isUpcomingMeal).length
  const heldThisWeek = mineOrders.filter((order) => isUpcomingMeal(order) && enumShort(order.status) === 'pending_payment').length

  const dishCount = uniqueDishLibrary(dishes.records.filter((d) => {
    const owner = String(d.chef_profile_id ?? '')
    return Boolean(profileId) && owner === profileId
  })).length

  const weekIncomeMinor = weekEarnedMinor(payouts)

  const toggleOpen = () => {
    if (statusPending) return
    const next = !manualOpen
    const base = vendorRecord ?? { id: `vendor-${profileId}`, profile_id: profileId }
    setStatusError('')
    /* Flip the shared vendor row instantly so the sign updates now — in live too
       (confirmedUpsert), because echoUpsert no-ops there and the toggle would
       otherwise wait a projection sweep. */
    confirmedUpsert('profile.vendor_profiles', { ...base, is_open: next })
    if (!isLiveRuntime()) {
      void commands.profile.setVendorStatus(profileId, next).catch(() => undefined)
      return
    }
    setStatusPending(true)
    void commands.profile
      .setVendorStatus(profileId, next)
      .then((result) => {
        /* Replace the optimistic row with the persisted one: it carries the
           vendor's real id and its stored schedule. The command resolves to the
           row itself — reading `.data` (always undefined) is why this merge
           never happened and the synthetic row lingered. */
        const row = commandPayload<StoreRecord>(result)
        rememberVendorStatus(profileId, typeof row.is_open === 'boolean' ? row.is_open : next)
        if (row.id) confirmedUpsert('profile.vendor_profiles', { ...base, ...row })
      })
      .catch(() => {
        /* Revert and say so — never leave a fake state
           (see live-writes-must-surface-errors). */
        confirmedUpsert('profile.vendor_profiles', { ...base, is_open: manualOpen })
        setStatusError(
          `Could not go ${next ? 'online' : 'offline'}. Check your connection and try again.`,
        )
      })
      .finally(() => setStatusPending(false))
  }

  const statusTone: 'open' | 'away' | 'off' = effectiveOpen
    ? 'open'
    : manualOpen
      ? 'away'
      : 'off'
  const statusText = effectiveOpen
    ? 'Open — accepting orders'
    : manualOpen
      ? 'Online, but outside your hours — customers can only schedule ahead'
      : 'Offline — customers can’t see your kitchen right now'

  const itemNames = useOrderItemNames(prepQueue.slice(0, 3).map((order) => String(order.id)))
  const today = openWindowToday(schedule)
  const todayIso = ((new Date().getDay() + 6) % 7) + 1
  const todayLine = today
    ? today.open
      ? `Today ${to12h(today.openTime)}–${to12h(today.closeTime)}`
      : 'Closed today'
    : null
  const { account: payoutAccount } = useChefPayoutAccount(profileId)
  const catalog = useKitchenCatalog(profileId)
  const hasPhone = useHasPhone(profileId)
  const verificationStatus = String(vendorRecord?.verification_status ?? 'unverified').toLowerCase()
  const verificationLine: Record<string, string> = {
    verified: vendorRecord?.is_sick_paused
      ? 'Paused for illness. Customers see you as closed.'
      : 'Your kitchen is verified and visible to customers.',
    pending: 'Your documents are in review.',
    suspended: 'Sales are paused while a hygiene report is reviewed.',
    rejected: 'Changes are needed before you can sell.',
  }

  return (
    <Screen header={<PageHeader title={page.title} accentDot />}>
      <OrderAlertsNudge profileId={profileId} />
      {!catalog.loading && (
        <KitchenSetupChecklist
          hasLocation={catalog.locations.length > 0}
          hasPhoto={Boolean(String(vendorRecord?.avatar_image_url ?? '').trim())}
          hasPhone={hasPhone}
          hasHours={Boolean(vendorRecord?.schedule && String(vendorRecord.schedule) !== '{}' && String(vendorRecord.schedule) !== 'null')}
          dishCount={dishCount}
          hasPayoutAccount={Boolean(payoutAccount?.account_tail)}
          verificationRequired={!catalog.paused}
          verificationStatus={verificationStatus}
        />
      )}
      <CommandHero $padding="sm">
        <CommandTopRow>
          <div>
            <SectionHeading title="Kitchen status" />
            <StatusLine style={{ marginTop: 4 }}>
              <StatusDot $tone={statusTone} />
              <ChefStyle.Sub>{statusText}</ChefStyle.Sub>
            </StatusLine>
          </div>
          <MinimalButton
            size="sm"
            variant={manualOpen ? 'secondary' : 'primary'}
            onClick={toggleOpen}
            disabled={statusPending}
          >
            {manualOpen ? 'Go offline' : 'Go online'}
          </MinimalButton>
        </CommandTopRow>
        {statusError ? <ChefStyle.Sub role="alert">{statusError}</ChefStyle.Sub> : null}

        {schedule && (
          <div>
            <WeekStrip>
              {schedule.days.map((d, i) => (
                <DayChip key={d.day} $open={d.open} $today={d.day === todayIso}>
                  <DayChipLetter $open={d.open}>{WEEKDAY_INITIALS[i]}</DayChipLetter>
                  <DayChipState>{d.open ? shortHour(d.openTime) : '—'}</DayChipState>
                </DayChip>
              ))}
            </WeekStrip>
          </div>
        )}

        <CommandActionRow>
          <MinimalButton size="sm" variant="secondary" onClick={() => navigate('/kitchen-board')}>
            Kitchen board →
          </MinimalButton>
          <MinimalButton size="sm" variant="ghost" onClick={() => navigate('/schedule')}>
            {todayLine ? `${todayLine} · Manage hours` : 'Set working hours'} →
          </MinimalButton>
        </CommandActionRow>
      </CommandHero>

      <ShareKitchenCard />

      <ReadinessGrid>
        {/* Only while it matters: when verification is switched off, telling a
            chef to "verify to start selling" sent them chasing a step that
            gates nothing. */}
        {(!catalog.paused || verificationStatus !== 'unverified') && (
        <ReadinessCard $padding="sm">
          <SectionHeading
            title="Verification"
            actionLabel={verificationStatus === 'verified' ? 'Manage' : 'Continue'}
            onAction={() => navigate('/verification')}
          />
          <KitchenBadge tier={vendorRecord?.badge_tier} />
          <ChefStyle.Sub>
            {verificationLine[verificationStatus] ?? 'Verify your kitchen to start selling.'}
          </ChefStyle.Sub>
        </ReadinessCard>
        )}

        {/* Until an account exists, the setup checklist above carries this step. */}
        {payoutAccount && (
        <ReadinessCard $padding="sm">
          <SectionHeading
            title="Payout account"
            actionLabel={payoutAccount ? 'Manage' : 'Add account'}
            onAction={() => navigate('/wallet?tab=methods')}
          />
          {payoutAccount ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{payoutAccount.bank_name}</span>
              <span style={{ color: minimalVars.color.textSecondary, fontSize: '0.85rem' }}>
                ••••{payoutAccount.account_tail}
              </span>
              {payoutAccount.bank_confirmed ? (
                <MinimalBadge tone="success" size="sm">Bank verified</MinimalBadge>
              ) : (
                <MinimalBadge tone="info" size="sm">In review</MinimalBadge>
              )}
            </div>
          ) : null}
        </ReadinessCard>
        )}
      </ReadinessGrid>

      {/* Customers message about their orders and nothing on the kitchen side
          ever said so. This is the notice: unread conversations, with the one
          tap that opens them. */}
      {unreadThreads > 0 && (
        <MinimalAlert
          tone="info"
          title={`${unreadThreads} customer message${unreadThreads === 1 ? '' : 's'} waiting`}
          action={
            <MinimalButton size="sm" onClick={() => navigate('/inbox')}>
              Open messages
            </MinimalButton>
          }
        >
          {unreadThreads === 1
            ? 'A customer has written about their order.'
            : 'Customers have written about their orders.'}
        </MinimalAlert>
      )}

      <KpiRow
        /* Transform-only entrance: page content must never be faded in, because
           a mid-tween opacity both hides real text from a reader and drops it
           below the contrast floor while it animates. */
        initial={{ y: 6 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
        <StatGrid>
          <StatCardLink to="/orders?filter=today" aria-label="Orders today"><MinimalStatCard label="Orders today" value={ordersToday} /></StatCardLink>
          <StatCardLink to="/orders?filter=prep" aria-label="Prep queue"><MinimalStatCard label="Prep queue" value={prepQueue.length} /></StatCardLink>
          <StatCardLink to="/orders?filter=scheduled" aria-label="Scheduled orders · 7 days"><MinimalStatCard label="Scheduled orders · 7 days" value={scheduledThisWeek} footer={`${scheduledThisWeek - heldThisWeek} confirmed · ${heldThisWeek} awaiting payment`} /></StatCardLink>
          <StatCardLink to="/wallet?tab=earnings&period=week" aria-label="This week"><MinimalStatCard label="This week" value={<Money amount={weekIncomeMinor / 100} tone="brand" size="md" />} /></StatCardLink>
          <StatCardLink to="/menu" aria-label="Live menus"><MinimalStatCard label="Live menus" value={activeMenus} footer={<StatLinkRow><StatLink>{draftMenus > 0 ? `${draftMenus} drafts` : 'Manage'} →</StatLink></StatLinkRow>} /></StatCardLink>
          <StatCardLink to="/menu?view=library" aria-label="Dishes"><MinimalStatCard label="Dishes" value={dishCount} footer={<StatLinkRow><StatLink>{dishCount > 0 ? 'Manage dishes' : 'Add a dish'} →</StatLink></StatLinkRow>} /></StatCardLink>
        </StatGrid>
        <StatCardLink to="/promotions" aria-label="Kitchen promotions"><MinimalStatCard label="Kitchen promotions" value="Offer a discount" footer="View campaigns and their status →" /></StatCardLink>
      </KpiRow>

      <ChefOpsSnapshot
        ordersToday={ordersToday}
        prepCount={prepQueue.length}
        scheduledThisWeek={scheduledThisWeek}
        weekIncomeMinor={weekIncomeMinor}
      />

      <PrepTimeline orders={mineOrders} />

      <SectionHeading
        title={`Next up · ${prepQueue.length}`}
        actionLabel="Open dispatch"
        onAction={() => navigate('/orders')}
      />
      {prepQueue.length === 0 ? (
        <MinimalEmptyState
          icon={<RouteIcon />}
          title="No active orders"
          description="Accept orders, cook, and mark them collected here."
        />
      ) : (
        <ChefStyle.Stack>
          {prepQueue.slice(0, 3).map((order) => (
            <ChefOrderCard key={String(order.id)} order={order} itemNames={itemNames[String(order.id)]} />
          ))}
        </ChefStyle.Stack>
      )}
    </Screen>
  )
}

/* ── Dispatch page: focused on the order queue ──────────────────────── */

const DispatchBody = styled(motion.div)`
  display: grid;
  gap: ${minimalVars.space.xs};
  /* Pull the body up close to the filter bar — the Screen's md gap was too
     much when the body is just an empty state. */
  margin-top: -${minimalVars.space['2xs']};
`

/* The kitchen's working stages, in order, with a human label and a tone.
   Drives both the status pill and the progress strip on each order ticket, so a
   chef reads state and next action at a glance instead of a raw enum. */
const STAGE_ORDER = ['paid', 'accepted', 'preparing', 'ready', 'delivery_requested', 'in_delivery'] as const
const STAGE_META: Record<string, { label: string; tone: MinimalTone }> = {
  paid: { label: 'New — needs accepting', tone: 'info' },
  accepted: { label: 'Accepted', tone: 'success' },
  preparing: { label: 'Preparing', tone: 'warning' },
  ready: { label: 'Ready for pickup', tone: 'success' },
  delivery_requested: { label: 'Delivery being arranged', tone: 'info' },
  in_delivery: { label: 'With delivery person', tone: 'info' },
  delivered: { label: 'Delivered', tone: 'success' },
  completed: { label: 'Completed', tone: 'success' },
}
const stageMeta = (status: string, choice?: unknown): { label: string; tone: MinimalTone } =>
  status === 'completed' && enumShort(choice) === 'self_pickup' ? { label: 'Collected', tone: 'success' } : STAGE_META[status] ?? { label: status.replace(/_/g, ' '), tone: 'neutral' }

const StageStrip = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 10px;
`
const StageSeg = styled.span<{ $on: boolean }>`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: ${({ $on }) => ($on ? minimalVars.color.success : minimalVars.color.borderSubtle)};
`
const DispatchMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
  color: ${minimalVars.color.textSecondary};
  font-size: ${minimalVars.typography.captionSize};
  & > svg {
    flex-shrink: 0;
  }
`
const DispatchHeaderBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${minimalVars.space.xs};
  width: 100%;
  background: none;
  border: 0;
  padding: 0;
  min-height: 44px;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid ${minimalVars.color.brand};
    outline-offset: 3px;
    border-radius: 4px;
  }
`
const DispatchActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: ${minimalVars.space['2xs']};
  flex: 1;
  min-width: 220px;

  > button {
    min-height: 40px;
    flex: 1 1 150px;
  }

  /* 620px matches no named stop; it sits between the hand and page stops. */
  @media (max-width: 620px) {
    width: 100%;
    min-width: 0;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));

    > button:last-child:nth-child(odd) {
      grid-column: 1 / -1;
    }
  }
`

const DispatchStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${minimalVars.space.xs};
  margin-top: ${minimalVars.space.xs};
  flex-wrap: wrap;
`

export const ChefDispatchPage = () => {
  const page = usePageContext('/orders')
  const { profileId } = useChooseChow()
  const [params, setParams] = useSearchParams()
  const rawFilter = params.get('filter')
  const filter: DispatchFilter = DISPATCH_FILTERS.some((item) => item.value === rawFilter) ? rawFilter as DispatchFilter : 'all'
  const date = params.get('date') ?? ''
  const orders = useMarketplaceOrderSnapshot() as { records: readonly StoreRecord[] }

  const queue = useMemo(() => {
    const mineOrders = orders.records.filter((o) => {
      if (isLiveRuntime()) {
        const id = String(o.id ?? '')
        if (id.startsWith('order-') || id.includes('-prototype')) return false
        return String(o.merchant_profile_id ?? '') === profileId
      }
      return true
    })
    const active = mineOrders.filter((order) =>
      isPrepOrder(order) || ['delivery_requested', 'in_delivery'].includes(enumShort(order.status)))
    if (filter === 'today') return sortByScheduled(mineOrders.filter(isConfirmedToday))
    if (filter === 'all') return sortByScheduled(active)
    if (filter === 'prep') return sortByScheduled(mineOrders.filter(isPrepOrder))
    if (filter === 'timeline') {
      const keys = new Set(buildPrepDays(mineOrders).map(day => day.key))
      return sortByScheduled(mineOrders.filter(order => {
        const key = prepDayKey(order)
        return key && keys.has(key) && (!date || key === date)
      }))
    }
    if (filter === 'scheduled') {
      return sortByScheduled(mineOrders.filter(isUpcomingMeal).filter((o) => !date || isoDay(new Date(String(o.scheduled_at))) === date))
    }
    return sortByScheduled(active.filter((o) => enumShort(o.status) === filter))
  }, [orders.records, filter, profileId, date])

  const groups = useMemo(() => groupBySchedule(queue), [queue])
  const itemNames = useOrderItemNames(queue.map((order) => String(order.id)))

  return (
    <Screen header={<PageHeader title={page.title} accentDot />}>
      <MinimalFilterBar
        ariaLabel="Filter dispatch queue"
        options={DISPATCH_FILTERS}
        value={filter}
        onChange={(next) => setParams(next === 'all' ? {} : { filter: next })}
      />

      {['scheduled', 'timeline'].includes(filter) && <MinimalAlert tone="info" title={date ? `Meals for ${date}` : 'Next 7 days'}>
        This count includes confirmed orders and future orders awaiting payment. Prepare confirmed orders only.
        {date && <MinimalButton variant="ghost" onClick={() => setParams({ filter })}>Show all 7 days</MinimalButton>}
      </MinimalAlert>}
      <DispatchBody
        key={filter}
        /* Transform-only entrance: page content must never be faded in, because
           a mid-tween opacity both hides real text from a reader and drops it
           below the contrast floor while it animates. */
        initial={{ y: 4 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
      >
        {queue.length === 0 ? (
          <MinimalEmptyState
            icon={<RouteIcon />}
            title="No matching orders"
            description="Switch a filter, or wait for the next order to land."
          />
        ) : (
          groups.map((group) =>
            group.records.length === 0 ? null : (
              <div key={group.label}>
                <SectionHeading title={`${group.label} · ${group.records.length}`} />
                <ChefStyle.Stack>
                  {group.records.map((order) => (
                    <ChefOrderCard key={String(order.id)} order={order} itemNames={itemNames[String(order.id)]} />
                  ))}
                </ChefStyle.Stack>
              </div>
            ),
          )
        )}
      </DispatchBody>
    </Screen>
  )
}

export const ChefOrderCard = ({ order, itemNames }: { order: StoreRecord; itemNames?: readonly string[] }) => {
  const navigate = useNavigate()
  const { profileId } = useChooseChow()
  const unreadMap = useUnreadOrdersMap(profileId, 'chef')
  const status = enumShort(order.status, 'paid')
  const step = orderStep(order)
  const meta = stageMeta(status, order.delivery_choice)
  const stages: readonly string[] = enumShort(order.delivery_choice) === 'self_pickup'
    ? ['paid', 'accepted', 'preparing', 'ready', 'completed'] : STAGE_ORDER
  const stageIdx = stages.indexOf(status)
  const scheduledAt = order.scheduled_at ? String(order.scheduled_at) : undefined
  const orderId = String(order.id ?? '')
  const unreadCount = unreadMap[orderId]?.unreadCount ?? 0
  const openDetail = () => orderId && navigate(`/orders/${encodeURIComponent(orderId)}`)
  const [expanded, setExpanded] = useState(false)
  const { detail, loading, error, retry } = useChefOrderDetail(expanded ? orderId : '')
  const advance = useOrderAdvance(order, profileId)
  return (
    <PremiumChefCard $padding="sm">
      <DispatchHeaderBtn type="button" aria-expanded={expanded} aria-controls={`ticket-${orderId}`} onClick={() => setExpanded(!expanded)}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <ChefStyle.Ref>{orderReference(order, 'Order #')}</ChefStyle.Ref>
            {unreadCount > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: 'rgba(230, 20, 69, 0.12)',
                  color: '#E61445',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                💬 {unreadCount} new
              </span>
            )}
          </div>
          <ChefStyle.Sub>{orderSummary(order, itemNames)}</ChefStyle.Sub>
          <ChefStyle.Sub>{expanded ? 'Hide order details −' : 'Expand order details +'}</ChefStyle.Sub>
        </div>
        <Money amount={Number(order.total_minor ?? 0) / 100} tone="brand" size="sm" />
      </DispatchHeaderBtn>
      {scheduledAt && (
        <DispatchMeta>
          <ClockIcon size="sm" aria-hidden />
          {scheduleMessage('chef', scheduledAt)}
        </DispatchMeta>
      )}
      {stageIdx >= 0 && (
        <StageStrip
          aria-hidden
          role="presentation"
          title={`Stage ${stageIdx + 1} of ${stages.length}`}
        >
          {stages.map((s, i) => (
            <StageSeg key={s} $on={i <= stageIdx} />
          ))}
        </StageStrip>
      )}
      <DispatchStatus>
        <MinimalBadge tone={meta.tone} emphasis="soft" size="sm">
          {meta.label}
        </MinimalBadge>
        <DispatchActions>
          <MinimalButton
            size="sm"
            variant={unreadCount > 0 ? 'primary' : 'secondary'}
            onClick={() => navigate(`/inbox?order=${encodeURIComponent(orderId)}`)}
          >
            {unreadCount > 0 ? `💬 ${unreadCount} new` : 'Chat'}
          </MinimalButton>
          <MinimalButton size="sm" variant="secondary" onClick={openDetail}>
            Details
          </MinimalButton>
          {step && (
            <MinimalButton size="sm" disabled={advance.busy} onClick={() => void advance.run(step.next)}>
              {advance.busy ? 'Updating…' : step.label}
            </MinimalButton>
          )}
        </DispatchActions>
      </DispatchStatus>
      {advance.error && <MinimalAlert tone="danger" title="Order not updated">{advance.error}</MinimalAlert>}
      {status === 'delivery_requested' && (
        <ChefStyle.Sub>Confirm collection after giving the order to the delivery person. The customer will see your update.</ChefStyle.Sub>
      )}
      {status === 'in_delivery' && (
        <ChefStyle.Sub>Confirm delivery after the delivery person reports that the customer received the order.</ChefStyle.Sub>
      )}
      {expanded && (
        <div id={`ticket-${orderId}`}>
          {loading && <ChefStyle.Sub role="status">Loading order details…</ChefStyle.Sub>}
          {error && <MinimalAlert tone="warning" title="Details unavailable" action={<MinimalButton size="sm" onClick={retry}>Retry</MinimalButton>}>{error}</MinimalAlert>}
          {detail && <TicketDetails detail={detail} />}
        </div>
      )}
    </PremiumChefCard>
  )
}

const TicketDetails = ({ detail }: { detail: StoreRecord }) => (
  <div>
    <DetailStyle.Line>
      <DetailStyle.Label>Customer</DetailStyle.Label>
      <DetailStyle.Value>{String(detail.customer_name || 'Customer')}</DetailStyle.Value>
    </DetailStyle.Line>
    <DetailStyle.Line>
      <DetailStyle.Label>{enumShort((detail.order as StoreRecord)?.delivery_choice) === 'self_pickup' ? 'Pickup' : 'Drop-off'}</DetailStyle.Label>
      <DetailStyle.Value>{String(detail.pickup_address || detail.dropoff_address || 'Address not provided')}</DetailStyle.Value>
    </DetailStyle.Line>
    <SectionHeading title="What to prepare" />
    {Array.isArray(detail.items) && detail.items.length > 0 ? detail.items.map((item: StoreRecord, index: number) => (
      <DetailStyle.Item key={index}>
        <DetailStyle.ItemMeta>
          <DetailStyle.ItemName>{Number(item.quantity ?? 1)}× {displayTitle(item.item_name) || 'Item'}</DetailStyle.ItemName>
          {Boolean(item.special_instructions) && <MinimalAlert tone="warning" title="Preparation note">{String(item.special_instructions)}</MinimalAlert>}
        </DetailStyle.ItemMeta>
      </DetailStyle.Item>
    )) : <ChefStyle.Sub>We can’t show this order’s dishes here. Open the full order before you cook.</ChefStyle.Sub>}
  </div>
)

const sortByScheduled = (records: readonly StoreRecord[]) =>
  [...records].sort((a, b) =>
    compareTimestamps(a.scheduled_at, b.scheduled_at, String(a.id ?? ''), String(b.id ?? ''), {
      nullsOrder: 'first',
    }),
  )

interface ScheduleGroup {
  label: string
  records: StoreRecord[]
}

const groupBySchedule = (records: readonly StoreRecord[]): ScheduleGroup[] => {
  const now: ScheduleGroup = { label: 'Now', records: [] }
  const later: ScheduleGroup = { label: 'Later today', records: [] }
  const week: ScheduleGroup = { label: 'This week', records: [] }
  const beyond: ScheduleGroup = { label: 'Beyond', records: [] }

  const ms = Date.now()
  const endOfToday = new Date()
  endOfToday.setHours(23, 59, 59, 999)
  const endToday = endOfToday.getTime()
  const sevenDays = ms + 7 * 24 * 60 * 60 * 1000

  for (const r of records) {
    const ts = r.scheduled_at ? Date.parse(String(r.scheduled_at)) : 0
    if (!ts || ts <= ms) {
      now.records.push(r)
    } else if (ts <= endToday) {
      later.records.push(r)
    } else if (ts <= sevenDays) {
      week.records.push(r)
    } else {
      beyond.records.push(r)
    }
  }
  return [now, later, week, beyond]
}

/* ── Order detail: everything the kitchen needs to fulfil one order ────── */

const DetailStyle = {
  Stack: styled.div`
    display: grid;
    gap: ${minimalVars.space.xs};
  `,
  Line: styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
    padding: ${minimalVars.space['2xs']} 0;
  `,
  Label: styled.span`
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.captionSize};
  `,
  Value: styled.span`
    color: ${minimalVars.color.textPrimary};
    font-weight: ${minimalVars.typography.weightMedium};
    text-align: right;
    overflow-wrap: anywhere;
  `,
  Customer: styled.div`
    display: flex;
    align-items: center;
    gap: ${minimalVars.space.xs};
  `,
  Avatar: styled.div<{ $src?: string }>`
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    border-radius: 50%;
    background-color: ${minimalVars.color.brandSoft};
    background-image: ${({ $src }) => {
    const resolved = mediaUrl($src, 128)
    return resolved ? `url(${resolved})` : 'none'
  }};
    background-size: cover;
    background-position: center;
    display: grid;
    place-items: center;
    color: ${minimalVars.color.brand};
    font-weight: ${minimalVars.typography.weightSemibold};
  `,
  Item: styled.div`
    display: flex;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
    padding: ${minimalVars.space['2xs']} 0;
    border-bottom: 1px solid ${minimalVars.color.borderSubtle};
    &:last-child {
      border-bottom: 0;
    }
  `,
  ItemMeta: styled.div`
    display: grid;
    gap: 2px;
    min-width: 0;
  `,
  ItemName: styled.span`
    font-weight: ${minimalVars.typography.weightSemibold};
    color: ${minimalVars.color.textPrimary};
  `,
  Actions: styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${minimalVars.space['2xs']};
    margin-top: ${minimalVars.space.xs};
  `,
  Hint: styled.p`
    margin: ${minimalVars.space.xs} 0 0;
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    line-height: 1.45;
  `,
  TotalLine: styled.div<{ $strong?: boolean }>`
    display: flex;
    justify-content: space-between;
    padding: ${minimalVars.space['2xs']} 0;
    font-weight: ${({ $strong }) =>
      $strong ? minimalVars.typography.weightSemibold : minimalVars.typography.weightRegular};
    color: ${({ $strong }) => ($strong ? minimalVars.color.textPrimary : minimalVars.color.textSecondary)};
  `,
}

const fmtWhen = (ts: unknown): string => {
  const raw = String(ts ?? '')
  if (!raw) return ''
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || '👤'

interface OrderView {
  order: StoreRecord
  items: StoreRecord[]
  customerName: string
  customerPhone: string
  customerCity: string
  customerAvatar: string
  dropoffAddress: string
  statusReason: string
  orderPriority: string
  discountMinor: number
}

/**
 * Merchant-facing order detail. Powered by the enriched `get_order` read
 * (proto OrderDetail) — who placed it, where it goes, and exactly what they
 * ordered — falling back to the lean projection row (+ profile lookup) when
 * offline so the prototype still renders something useful.
 */
export const ChefOrderDetailPage = () => {
  const { orderId = '' } = useParams()
  const navigate = useNavigate()
  const back = useBackNav()
  const { profileId } = useChooseChow()
  const orders = useMarketplaceOrderSnapshot() as { records: readonly StoreRecord[] }
  const profiles = useChowProfileSnapshot() as { records: readonly StoreRecord[] }
  const { detail, loading, error, retry, setDetail } = useChefOrderDetail(orderId)

  const projected = useMemo(
    () => orders.records.find((o) => String(o.id ?? '') === orderId),
    [orders.records, orderId],
  )

  const view = useMemo<OrderView | null>(() => {
    const live = (detail?.order as StoreRecord | undefined) ?? undefined
    const newerProjection = projected && live && isAfterTimestamp(projected.updated_at, live.updated_at)
    const order = newerProjection ? { ...live, ...projected } : live ?? projected
    if (!order) return null
    const items = (detail?.items as StoreRecord[] | undefined) ?? []
    const customer = profiles.records.find(
      (p) => String(p.id ?? '') === String(order.customer_profile_id ?? ''),
    )
    return {
      order,
      items,
      customerName:
        String(detail?.customer_name ?? '') || String(customer?.display_name ?? '') || 'Customer',
      customerPhone: String(detail?.customer_phone ?? '') || String(customer?.phone ?? ''),
      customerCity: String(detail?.customer_city ?? '') || String(customer?.city ?? ''),
      customerAvatar: String(detail?.customer_avatar_url ?? '') || String(customer?.avatar_url ?? ''),
      dropoffAddress: String(detail?.dropoff_address ?? ''),
      statusReason: String(detail?.status_reason ?? ''),
      orderPriority: enumShort(detail?.order_priority ?? order.order_priority ?? 'standard'),
      discountMinor: Number(detail?.discount_applied_minor ?? 0),
    }
  }, [detail, projected, profiles.records])

  const advance = useOrderAdvance(view?.order ?? { id: orderId }, profileId)

  if (!view) {
    return (
      <Screen header={<PageHeader title="Order" accentDot onBack={back} />}>
        {loading ? (
          <MinimalAlert tone="info" title="Loading order…">
            Fetching the order details.
          </MinimalAlert>
        ) : (
          <MinimalEmptyState
            icon={<RouteIcon />}
            title="Order not found"
            description={error || "This order isn't available, or you don't have access to it."}
            action={<DetailStyle.Actions><MinimalButton onClick={retry}>Retry</MinimalButton><MinimalButton variant="secondary" onClick={back}>Back to dispatch</MinimalButton></DetailStyle.Actions>}
          />
        )}
      </Screen>
    )
  }

  const { order } = view
  const status = enumShort(order.status, 'paid')
  const step = orderStep(order)
  const subtotal = Number(order.subtotal_minor ?? 0)
  // No delivery is charged until ChowDash dispatch is live; a historical
  // order's recorded delivery is ChooseChow's, so it never counts as earnings.
  const legacyCharge = Number(order.delivery_fee_minor ?? 0)
  const serviceFee = Number(order.service_fee_minor ?? 0)
  const total = Number(order.total_minor ?? 0)
  // The chef's share, derived exactly as the payout split derives it.
  const earnings = Math.max(0, total - serviceFee - legacyCharge)
  const scheduledAt = order.scheduled_at ? String(order.scheduled_at) : ''

  return (
    <Screen
      header={<PageHeader title={orderReference(order, 'Order #')} accentDot onBack={back} />}
    >
      <DetailStyle.Stack>
        {error && <MinimalAlert tone="warning" title="Some details are unavailable" action={<MinimalButton onClick={retry}>Retry</MinimalButton>}>{error}</MinimalAlert>}
        {advance.error && <MinimalAlert tone="danger" title="Order not updated">{advance.error}</MinimalAlert>}
        {/* Who ordered */}
        <MinimalCard padding="sm" enter={false}>
          <SectionHeading title="Customer" />
          <DetailStyle.Customer>
            <DetailStyle.Avatar $src={view.customerAvatar || undefined}>
              {!view.customerAvatar && initials(view.customerName)}
            </DetailStyle.Avatar>
            <div style={{ minWidth: 0 }}>
              <DetailStyle.ItemName>{view.customerName}</DetailStyle.ItemName>
              <ChefStyle.Sub>
                {[view.customerCity, view.customerPhone].filter(Boolean).join(' · ') ||
                  'No contact details on file'}
              </ChefStyle.Sub>
            </div>
          </DetailStyle.Customer>
          {/* Messaging is the only channel that works both ways for an order,
              and until now nothing on the kitchen side linked to it: a customer
              could write about their order and the chef had no route to the
              conversation short of Profile → Inbox. */}
          <DetailStyle.Actions>
            {view.customerPhone && (
              <MinimalButton
                size="sm"
                variant="secondary"
                onClick={() => window.open(`tel:${view.customerPhone}`)}
              >
                Call customer
              </MinimalButton>
            )}
            <MinimalButton
              size="sm"
              variant="secondary"
              onClick={() => navigate(`/inbox?order=${encodeURIComponent(String(order.id ?? ''))}`)}
            >
              Message customer
            </MinimalButton>
          </DetailStyle.Actions>
          <DetailStyle.Hint>
            Agree the collection time here, or take the customer’s rider details if they are sending someone.
          </DetailStyle.Hint>
        </MinimalCard>

        {/* Status + timeline */}
        <MinimalCard padding="sm" enter={false}>
          <ChefStyle.Row>
            <SectionHeading title="Status" />
            <MinimalBadge tone={stageMeta(status, order.delivery_choice).tone} emphasis="soft" size="sm">
              {stageMeta(status, order.delivery_choice).label}
            </MinimalBadge>
          </ChefStyle.Row>
          <DetailStyle.Line>
            <DetailStyle.Label>Placed</DetailStyle.Label>
            <DetailStyle.Value>{fmtWhen(order.created_at) || '—'}</DetailStyle.Value>
          </DetailStyle.Line>
          <DetailStyle.Line>
            <DetailStyle.Label>Last update</DetailStyle.Label>
            <DetailStyle.Value>{fmtWhen(order.updated_at) || '—'}</DetailStyle.Value>
          </DetailStyle.Line>
          {view.statusReason && (
            <DetailStyle.Line>
              <DetailStyle.Label>Note</DetailStyle.Label>
              <DetailStyle.Value>{view.statusReason.replace(/_/g, ' ')}</DetailStyle.Value>
            </DetailStyle.Line>
          )}
          {step && (
            <div style={{ marginTop: 8 }}>
              <MinimalButton
                size="sm"
                fullWidth
                disabled={advance.busy}
                onClick={async () => {
                  const updated = await advance.run(step.next)
                  if (updated) setDetail((prev) => (prev?.order as StoreRecord | undefined)?.id === updated.id ? { ...prev, order: updated } : prev)
                }}
              >
                {advance.busy ? 'Updating…' : step.label}
              </MinimalButton>
            </div>
          )}
        </MinimalCard>

        {Boolean(order.promo_code) && <MinimalAlert tone="info" title={`Promo: ${String(order.promo_code)}`}>
          {Number(detail?.chef_promo_discount_minor ?? 0) > 0
            ? `Your kitchen funds ₦${(Number(detail?.chef_promo_discount_minor) / 100).toLocaleString()} of this discount.`
            : 'The total already includes the discount.'}
        </MinimalAlert>}

        {/* What they ordered */}
        <MinimalCard padding="sm" enter={false}>
          <SectionHeading title={`Items · ${view.items.length}`} />
          {view.items.length === 0 ? (
            <ChefStyle.Sub>
              Line items aren't available for this order yet
              {isLiveRuntime() ? '.' : ' (offline preview).'}
            </ChefStyle.Sub>
          ) : (
            view.items.map((it, i) => {
              const qty = Number(it.quantity ?? 1)
              const lineTotal = Number(it.line_total_minor ?? Number(it.unit_amount_minor ?? 0) * qty)
              const note = String(it.special_instructions ?? '')
              return (
                <DetailStyle.Item key={`${String(it.item_ref ?? i)}-${i}`}>
                  <DetailStyle.ItemMeta>
                    <DetailStyle.ItemName>
                      {qty}× {displayTitle(it.item_name) || 'Item'}
                    </DetailStyle.ItemName>
                    {note && <ChefStyle.Sub>“{note}”</ChefStyle.Sub>}
                  </DetailStyle.ItemMeta>
                  <Money amount={lineTotal / 100} tone="brand" size="sm" />
                </DetailStyle.Item>
              )
            })
          )}
        </MinimalCard>

        {/* Delivery + fulfilment */}
        <MinimalCard padding="sm" enter={false}>
          <SectionHeading title={enumShort(order.delivery_choice) === 'self_pickup' ? 'Kitchen pickup' : 'Delivery'} />
          <DetailStyle.Line>
            <DetailStyle.Label>Method</DetailStyle.Label>
            <DetailStyle.Value>{enumShort(order.delivery_choice) === 'self_pickup' ? 'Customer collection' : enumLabel(order.delivery_choice, 'standard')}</DetailStyle.Value>
          </DetailStyle.Line>
          {view.orderPriority && view.orderPriority !== 'standard' && (
            <DetailStyle.Line>
              <DetailStyle.Label>Priority</DetailStyle.Label>
              <DetailStyle.Value>{view.orderPriority}</DetailStyle.Value>
            </DetailStyle.Line>
          )}
          {scheduledAt && (
            <DetailStyle.Line>
              <DetailStyle.Label>Scheduled</DetailStyle.Label>
              <DetailStyle.Value>{fmtWhen(scheduledAt)}</DetailStyle.Value>
            </DetailStyle.Line>
          )}
          {enumShort(order.delivery_choice) === 'self_pickup' && <DetailStyle.Line><DetailStyle.Label>Pickup address</DetailStyle.Label><DetailStyle.Value>{String(order.pickup_address || detail?.pickup_address || 'Address unavailable')}</DetailStyle.Value></DetailStyle.Line>}
          {view.dropoffAddress && (
            <DetailStyle.Line>
              <DetailStyle.Label>Drop-off</DetailStyle.Label>
              <DetailStyle.Value>{view.dropoffAddress}</DetailStyle.Value>
            </DetailStyle.Line>
          )}
        </MinimalCard>

        {/* Money */}
        <MinimalCard padding="sm" enter={false}>
          <SectionHeading title="Payment" />
          <DetailStyle.TotalLine>
            <span>Food</span>
            <Money amount={subtotal / 100} size="sm" />
          </DetailStyle.TotalLine>
          {view.discountMinor > 0 && (
            <DetailStyle.TotalLine>
              <span>Discount</span>
              <span>-<Money amount={view.discountMinor / 100} size="sm" /></span>
            </DetailStyle.TotalLine>
          )}
          <DetailStyle.TotalLine $strong>
            <span>You receive</span>
            <Money amount={earnings / 100} tone="brand" size="md" />
          </DetailStyle.TotalLine>
          <DetailStyle.Line>
            <DetailStyle.Label>Customer paid</DetailStyle.Label>
            <DetailStyle.Value>
              <Money amount={total / 100} size="sm" /> incl. <Money amount={serviceFee / 100} size="sm" /> ChooseChow service fee
            </DetailStyle.Value>
          </DetailStyle.Line>
        </MinimalCard>
      </DetailStyle.Stack>
    </Screen>
  )
}
